type Point = [number, number];

const f = (p: Point) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`;

const roundedPoly = (pts: Point[], r: number) => {
  const n = pts.length;
  let d = '';
  for (let i = 0; i < n; i++) {
    const p = pts[(i - 1 + n) % n];
    const v = pts[i];
    const q = pts[(i + 1) % n];
    const t1 = Math.min(r / Math.hypot(p[0] - v[0], p[1] - v[1]), 0.5);
    const t2 = Math.min(r / Math.hypot(q[0] - v[0], q[1] - v[1]), 0.5);
    const a: Point = [v[0] + (p[0] - v[0]) * t1, v[1] + (p[1] - v[1]) * t1];
    const b: Point = [v[0] + (q[0] - v[0]) * t2, v[1] + (q[1] - v[1]) * t2];
    d += `${(i ? 'L' : 'M') + f(a)}Q${f(v)} ${f(b)}`;
  }
  return `${d}Z`;
};

const sampled = (rx: (t: number) => Point, n = 240) => {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const [x, y] = rx((i / n) * Math.PI * 2);
    pts.push(f([100 + x, 100 + y]));
  }
  return `M${pts.join('L')}Z`;
};

const seal = (lobes: number, R: number, r: number) => {
  const pts: Point[] = [];
  for (let i = 0; i < lobes * 2; i++) {
    const a = ((-90 + (i * 180) / lobes) * Math.PI) / 180;
    const rad = i % 2 ? r : R;
    pts.push([100 + rad * Math.cos(a), 100 + rad * Math.sin(a)]);
  }
  return roundedPoly(pts, 60);
};

const superellipse = (t: number): Point => {
  const c = Math.cos(t);
  const s = Math.sin(t);
  return [80 * Math.sign(c) * Math.abs(c) ** 0.5, 80 * Math.sign(s) * Math.abs(s) ** 0.5];
};

const flower = (t: number): Point => {
  const r = 64 + 22 * Math.abs(Math.cos(3 * t)) ** 0.65;
  return [r * Math.sin(t), -r * Math.cos(t)];
};

export const BADGE_SHAPES = {
  none: { label: 'None', d: null as string | null },
  circle: { label: 'Circle', d: sampled((t) => [82 * Math.cos(t), 82 * Math.sin(t)]) },
  square: { label: 'Square', d: sampled(superellipse) },
  shield: {
    label: 'Shield',
    d: roundedPoly(
      [
        [100, 14],
        [174, 36],
        [176, 96],
        [160, 146],
        [100, 188],
        [40, 146],
        [24, 96],
        [26, 36],
      ],
      26
    ),
  },
  rosette: { label: 'Rosette', d: seal(8, 90, 72) },
  flower: { label: 'Flower', d: sampled(flower) },
};

export const TAIL_L = roundedPoly(
  [
    [78, 136],
    [40, 210],
    [66, 205],
    [80, 226],
    [122, 154],
  ],
  8
);
export const TAIL_R = roundedPoly(
  [
    [122, 136],
    [160, 210],
    [134, 205],
    [120, 226],
    [78, 154],
  ],
  8
);

const LEAF = 'M-12 0C-8-8.5 5-9.5 12 0C5 9.5-8 8.5-12 0Z';

const leafPair = (fill: string, x: number, y: number, tan: number, k: string) =>
  [-40, 40]
    .map(
      (tilt) =>
        `<path d="${LEAF}" fill="${fill}" transform="translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${(
          tan + tilt
        ).toFixed(1)}) translate(11 0) scale(${k})"/>`
    )
    .join('');

export const wreath = (fill: string) => {
  const R = 82;
  const leaves = [0, 1, 2, 3, 4, 5].map((i) => ({ deg: 36 + i * 21, k: 1.15 - i * 0.05 }));
  let out = '';
  [-1, 1].forEach((side) => {
    const at = (deg: number): Point => {
      const p = (deg * Math.PI) / 180;
      return [100 + side * R * Math.sin(p), 100 + R * Math.cos(p)];
    };
    const tangent = (phi: number) => (Math.atan2(-Math.sin(phi), side * Math.cos(phi)) * 180) / Math.PI;
    out += `<path d="M${f(at(28))}A${R} ${R} 0 0 ${side < 0 ? 1 : 0} ${f(
      at(150)
    )}" fill="none" stroke="${fill}" stroke-width="4.5" stroke-linecap="round"/>`;
    leaves.forEach(({ deg, k }) => {
      const [x, y] = at(deg);
      out += leafPair(fill, x, y, tangent((deg * Math.PI) / 180), k.toFixed(2));
    });
    const [tx, ty] = at(150);
    out += `<path d="${LEAF}" fill="${fill}" transform="translate(${tx.toFixed(1)} ${ty.toFixed(1)}) rotate(${tangent(
      (150 * Math.PI) / 180
    ).toFixed(1)}) translate(10 0) scale(.85)"/>`;
  });
  return out;
};

const DIGITS: Record<string, string> = {
  0: 'M10 10v4a2 2 0 1 0 4 0v-4a2 2 0 1 0 -4 0z',
  1: 'M11 9.5l1.5 -1.5v8',
  2: 'M10 8h3a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 0 -1 1v2a1 1 0 0 0 1 1h3',
  3: 'M10 9a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1',
  4: 'M10 8v3a1 1 0 0 0 1 1h3M14 8v8',
  5: 'M10 15a1 1 0 0 0 1 1h2a1 1 0 0 0 1 -1v-2a1 1 0 0 0 -1 -1h-3v-4h4',
  6: 'M14 9a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v6a1 1 0 0 0 1 1h2a1 1 0 0 0 1 -1v-2a1 1 0 0 0 -1 -1h-3',
  7: 'M10 8h4l-2 8',
  8: 'M12 12h-1a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-1h1a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1',
  9: 'M10 15a1 1 0 0 0 1 1h2a1 1 0 0 0 1 -1v-6a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v2a1 1 0 0 0 1 1h3',
};

export const digits = (v: string, cy: number, maxW: number, ink: string) => {
  const chars = v.split('').filter((ch) => DIGITS[ch]);
  if (!chars.length) return '';
  const adv = 6.6;
  const w = chars.length * 4 + (chars.length - 1) * 2.6;
  const k = Math.min(5.8, maxW / (w + 2));
  const paths = chars
    .map((ch, i) => `<path d="${DIGITS[ch]}" transform="translate(${(i * adv - 10).toFixed(2)} -8)"/>`)
    .join('');
  return `<g transform="translate(${(100 - (k * w) / 2).toFixed(2)} ${(cy - 4 * k).toFixed(2)}) scale(${k.toFixed(
    3
  )})" fill="none" stroke="${ink}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</g>`;
};
